import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Typography,
  Button,
  Input,
  Textarea,
  Select,
  Option,
  FormControl,
  FormLabel,
  Stack,
  Grid,
  Alert,
  Chip,
  FormHelperText,
  CircularProgress,
} from '@mui/joy';
import { FloppyDisk as Save, XCircle as Cancel, Plus as Add } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { subcontractorsApi, specialtiesApi } from '../../lib/apiSubcontractors';
import { apiClient } from '../../lib/api';
import type { Subcontractor, SubcontractorFormData } from '../../types/subcontractors';
import type { ParsedAddress } from '../../types';
import { applyPrefill } from '../../utils/prefill';
import { AddressAutocompleteInput } from '../common/AddressAutocompleteInput';
import {
  WebsiteInput,
  splitWebsiteUrl,
  joinWebsiteUrl,
  type WebsiteProtocol,
} from '../common/WebsiteInput';

interface SubcontractorFormProps {
  subcontractor?: Subcontractor;
  editId?: number;
  onSuccess?: (subcontractor: Subcontractor) => void;
  onCancel?: () => void;
}

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

export const SubcontractorForm: React.FC<SubcontractorFormProps> = ({
  subcontractor,
  editId,
  onSuccess,
  onCancel,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEditMode = !!editId || !!subcontractor;

  // Fetch specialties
  const { data: specialties = [], refetch: refetchSpecialties } = useQuery({
    queryKey: ['subcontractor-specialties'],
    queryFn: () => specialtiesApi.list(),
  });

  // Most subs work the market the company works, so the main office state is
  // the right first guess - and it stays a dropdown for the ones who don't.
  const { data: organization } = useQuery({
    queryKey: ['organization'],
    queryFn: async () => {
      const response = await apiClient.get<{ main_office_state?: string }>('/api/organizations/current/');
      return response.data;
    },
    enabled: !isEditMode,
  });

  // State for creating new specialty
  const [showNewSpecialtyForm, setShowNewSpecialtyForm] = useState(false);
  const [newSpecialtyName, setNewSpecialtyName] = useState('');
  const [newSpecialtyDescription, setNewSpecialtyDescription] = useState('');
  const [isCreatingSpecialty, setIsCreatingSpecialty] = useState(false);
  const [specialtyError, setSpecialtyError] = useState('');

  // Form state
  const [formData, setFormData] = useState<SubcontractorFormData>({
    company_name: '',
    dba_name: '',
    contact_person: '',
    email: '',
    phone: '',
    secondary_phone: '',
    website: '',
    tax_id: '',
    license_number: '',
    license_expiry: '',
    address_1: '',
    address_2: '',
    city: '',
    state: '',
    zip_code: '',
    specialty_ids: [],
    payment_terms: 'net_30',
    custom_payment_terms: '',
    preferred_payment_method: 'check',
    bank_name: '',
    bank_routing_number: '',
    bank_account_number: '',
    status: 'active',
    rating: undefined,
    notes: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Website is stored as a single URL string, but edited as separate protocol + domain
  const [websiteProtocol, setWebsiteProtocol] = useState<WebsiteProtocol>('https://');
  const [websiteDomain, setWebsiteDomain] = useState<string>('');

  // Load existing data in edit mode
  useEffect(() => {
    if (subcontractor) {
      const { protocol, domain } = splitWebsiteUrl(subcontractor.website);
      setWebsiteProtocol(protocol);
      setWebsiteDomain(domain);
      setFormData({
        company_name: subcontractor.company_name,
        dba_name: subcontractor.dba_name || '',
        contact_person: subcontractor.contact_person,
        email: subcontractor.email,
        phone: subcontractor.phone,
        secondary_phone: subcontractor.secondary_phone || '',
        website: subcontractor.website || '',
        tax_id: subcontractor.tax_id || '',
        license_number: subcontractor.license_number || '',
        license_expiry: subcontractor.license_expiry || '',
        address_1: subcontractor.address_1,
        address_2: subcontractor.address_2 || '',
        city: subcontractor.city,
        state: subcontractor.state,
        zip_code: subcontractor.zip_code,
        specialty_ids: subcontractor.specialties.map(s => s.id),
        payment_terms: subcontractor.payment_terms,
        custom_payment_terms: subcontractor.custom_payment_terms || '',
        preferred_payment_method: subcontractor.preferred_payment_method,
        bank_name: subcontractor.bank_name || '',
        bank_routing_number: subcontractor.bank_routing_number || '',
        bank_account_number: subcontractor.bank_account_number || '',
        status: subcontractor.status,
        rating: subcontractor.rating || undefined,
        notes: subcontractor.notes || '',
      });
    }
  }, [subcontractor]);

  // Seed the state only while it is still blank, so an out-of-state sub the
  // user already picked a state for is never overwritten.
  useEffect(() => {
    if (isEditMode || !organization?.main_office_state) return;
    setFormData(prev => applyPrefill(prev, { state: organization.main_office_state }));
  }, [organization, isEditMode]);

  /** Fill city/state/zip from the picked place, keeping every field editable. */
  const handleAddressSelect = (parsed: ParsedAddress) => {
    setFormData(prev => ({
      ...prev,
      address_1: parsed.address_1 || prev.address_1,
      city: parsed.city || prev.city,
      state: parsed.state || prev.state,
      zip_code: parsed.zip_code || prev.zip_code,
    }));
  };

  // Create/update mutation
  const saveMutation = useMutation({
    mutationFn: async (data: SubcontractorFormData) => {
      if (isEditMode) {
        const recordId = editId || subcontractor?.id;
        if (!recordId) throw new Error("Load the existing profile before saving.");
        return subcontractorsApi.update(recordId, data);
      } else {
        return subcontractorsApi.create(data);
      }
    },
    onSuccess: (savedSubcontractor) => {
      queryClient.invalidateQueries({ queryKey: ['subcontractors'] });
      if (onSuccess) {
        onSuccess(savedSubcontractor);
      } else {
        navigate(`/operations/subcontractors/${savedSubcontractor.id}`);
      }
    },
    onError: (error: any) => {
      if (error.responseData) {
        setErrors(error.responseData);
      } else if (error.response?.data) {
        setErrors(error.response.data);
      }
    },
  });

  const handleChange = (field: keyof SubcontractorFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleCreateSpecialty = async () => {
    if (!newSpecialtyName.trim()) {
      setSpecialtyError('Specialty name is required');
      return;
    }

    setIsCreatingSpecialty(true);
    setSpecialtyError('');

    try {
      const newSpecialty = await specialtiesApi.create({
        name: newSpecialtyName.trim(),
        description: newSpecialtyDescription.trim() || undefined,
      });

      // Refresh the specialties list
      await refetchSpecialties();

      // Add the new specialty to selected specialties
      setFormData(prev => ({
        ...prev,
        specialty_ids: [...(prev.specialty_ids || []), newSpecialty.id],
      }));

      // Reset form
      setNewSpecialtyName('');
      setNewSpecialtyDescription('');
      setShowNewSpecialtyForm(false);
    } catch (error: any) {
      console.error('Error creating specialty:', error);
      setSpecialtyError(error.response?.data?.name?.[0] || 'Failed to create specialty');
    } finally {
      setIsCreatingSpecialty(false);
    }
  };

  const handleCancelNewSpecialty = () => {
    setShowNewSpecialtyForm(false);
    setNewSpecialtyName('');
    setNewSpecialtyDescription('');
    setSpecialtyError('');
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.company_name.trim()) {
      newErrors.company_name = 'Company name is required';
    }
    if (!formData.contact_person.trim()) {
      newErrors.contact_person = 'Contact person is required';
    }
    // Email is optional, but validate format if provided
    if (formData.email && formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }
    // Address fields are now optional - no validation required

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      // Combine website protocol + domain into a single URL before sending
      const cleanedData = {
        ...formData,
        website: joinWebsiteUrl(websiteProtocol, websiteDomain),
      };
      
      // PATCH must retain explicit blanks so clearing a field clears its stored value.
      cleanedData.license_expiry = cleanedData.license_expiry || null;
      saveMutation.mutate(cleanedData);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      navigate('/operations/subcontractors');
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Stack spacing={3}>
        {/* Company Information */}
        <Card>
          <Typography level="title-lg" sx={{ mb: 2 }}>
            Company Information
          </Typography>
          <Grid container spacing={2}>
            <Grid xs={12} md={6}>
              <FormControl error={!!errors.company_name}>
                <FormLabel required>Company Name</FormLabel>
                <Input
                  value={formData.company_name}
                  onChange={(e) => handleChange('company_name', e.target.value)}
                />
                {errors.company_name && (
                  <FormHelperText>{errors.company_name}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid xs={12} md={6}>
              <FormControl>
                <FormLabel>DBA Name</FormLabel>
                <Input
                  value={formData.dba_name}
                  onChange={(e) => handleChange('dba_name', e.target.value)}
                />
              </FormControl>
            </Grid>
            <Grid xs={12} md={6}>
              <FormControl error={!!errors.contact_person}>
                <FormLabel required>Contact Person</FormLabel>
                <Input
                  value={formData.contact_person}
                  onChange={(e) => handleChange('contact_person', e.target.value)}
                />
                {errors.contact_person && (
                  <FormHelperText>{errors.contact_person}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid xs={12} md={6}>
              <FormControl error={!!errors.email}>
                <FormLabel>Email</FormLabel>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                />
                {errors.email && (
                  <FormHelperText>{errors.email}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid xs={12} md={4}>
              <FormControl error={!!errors.phone}>
                <FormLabel required>Primary Phone</FormLabel>
                <Input
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                />
                {errors.phone && (
                  <FormHelperText>{errors.phone}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid xs={12} md={4}>
              <FormControl>
                <FormLabel>Secondary Phone</FormLabel>
                <Input
                  value={formData.secondary_phone}
                  onChange={(e) => handleChange('secondary_phone', e.target.value)}
                />
              </FormControl>
            </Grid>
            <Grid xs={12} md={4}>
              <WebsiteInput
                protocol={websiteProtocol}
                domain={websiteDomain}
                onProtocolChange={setWebsiteProtocol}
                onDomainChange={setWebsiteDomain}
              />
            </Grid>
          </Grid>
        </Card>

        {/* Address */}
        <Card>
          <Typography level="title-lg" sx={{ mb: 2 }}>
            Address
          </Typography>
          <Grid container spacing={2}>
            <Grid xs={12}>
              <FormControl error={!!errors.address_1}>
                <FormLabel>Street Address</FormLabel>
                <AddressAutocompleteInput
                  value={formData.address_1 || ''}
                  onChange={(value) => handleChange('address_1', value)}
                  onAddressSelect={handleAddressSelect}
                />
                {errors.address_1 && (
                  <FormHelperText>{errors.address_1}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid xs={12}>
              <FormControl>
                <FormLabel>Address Line 2</FormLabel>
                <Input
                  value={formData.address_2}
                  onChange={(e) => handleChange('address_2', e.target.value)}
                  placeholder="Apt, Suite, Unit, etc."
                />
              </FormControl>
            </Grid>
            <Grid xs={12} md={5}>
              <FormControl error={!!errors.city}>
                <FormLabel>City</FormLabel>
                <Input
                  value={formData.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                />
                {errors.city && (
                  <FormHelperText>{errors.city}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid xs={12} md={3}>
              <FormControl error={!!errors.state}>
                <FormLabel>State</FormLabel>
                <Select
                  value={formData.state}
                  onChange={(_, value) => handleChange('state', value)}
                >
                  <Option value="">Select State</Option>
                  {US_STATES.map(state => (
                    <Option key={state} value={state}>{state}</Option>
                  ))}
                </Select>
                {errors.state && (
                  <FormHelperText>{errors.state}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid xs={12} md={4}>
              <FormControl error={!!errors.zip_code}>
                <FormLabel>ZIP Code</FormLabel>
                <Input
                  value={formData.zip_code}
                  onChange={(e) => handleChange('zip_code', e.target.value)}
                />
                {errors.zip_code && (
                  <FormHelperText>{errors.zip_code}</FormHelperText>
                )}
              </FormControl>
            </Grid>
          </Grid>
        </Card>

        {/* Business Details */}
        <Card>
          <Typography level="title-lg" sx={{ mb: 2 }}>
            Business Details
          </Typography>
          <Grid container spacing={2}>
            <Grid xs={12} md={4}>
              <FormControl>
                <FormLabel>Tax ID / EIN</FormLabel>
                <Input
                  value={formData.tax_id}
                  onChange={(e) => handleChange('tax_id', e.target.value)}
                />
              </FormControl>
            </Grid>
            <Grid xs={12} md={4}>
              <FormControl>
                <FormLabel>License Number</FormLabel>
                <Input
                  value={formData.license_number}
                  onChange={(e) => handleChange('license_number', e.target.value)}
                />
              </FormControl>
            </Grid>
            <Grid xs={12} md={4}>
              <FormControl>
                <FormLabel>License Expiry Date</FormLabel>
                <Input
                  type="date"
                  value={formData.license_expiry || ""}
                  onChange={(e) => handleChange('license_expiry', e.target.value)}
                />
              </FormControl>
            </Grid>
            <Grid xs={12}>
              <FormControl>
                <FormLabel>Specialties</FormLabel>
                <Stack spacing={1}>
                  <Select
                    multiple
                    value={formData.specialty_ids}
                    onChange={(_, value) => handleChange('specialty_ids', value)}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {(selected as any[]).map((option: any) => (
                          <Chip key={option.value} size="sm" variant="soft">
                            {option.label}
                          </Chip>
                        ))}
                      </Box>
                    )}
                  >
                    {specialties.map(spec => (
                      <Option key={spec.id} value={spec.id}>
                        {spec.name}
                      </Option>
                    ))}
                  </Select>
                  
                  {!showNewSpecialtyForm ? (
                    <Button
                      size="sm"
                      variant="plain"
                      startDecorator={<Add />}
                      onClick={() => setShowNewSpecialtyForm(true)}
                    >
                      Add New Specialty
                    </Button>
                  ) : (
                    <Card variant="soft" sx={{ p: 2 }}>
                      <Stack spacing={1.5}>
                        <Typography level="title-sm">Create New Specialty</Typography>
                        <FormControl error={!!specialtyError}>
                          <FormLabel>Name</FormLabel>
                          <Input
                            size="sm"
                            value={newSpecialtyName}
                            onChange={(e) => {
                              setNewSpecialtyName(e.target.value);
                              setSpecialtyError('');
                            }}
                            placeholder="e.g., Roofing, Plumbing, Electrical"
                            disabled={isCreatingSpecialty}
                          />
                          {specialtyError && (
                            <FormHelperText>{specialtyError}</FormHelperText>
                          )}
                        </FormControl>
                        <FormControl>
                          <FormLabel>Description (Optional)</FormLabel>
                          <Textarea
                            size="sm"
                            minRows={2}
                            value={newSpecialtyDescription}
                            onChange={(e) => setNewSpecialtyDescription(e.target.value)}
                            placeholder="Brief description of this specialty"
                            disabled={isCreatingSpecialty}
                          />
                        </FormControl>
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Button
                            size="sm"
                            variant="plain"
                            color="neutral"
                            onClick={handleCancelNewSpecialty}
                            disabled={isCreatingSpecialty}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            startDecorator={isCreatingSpecialty ? <CircularProgress size="sm" /> : <Add />}
                            onClick={handleCreateSpecialty}
                            disabled={isCreatingSpecialty}
                          >
                            {isCreatingSpecialty ? 'Creating...' : 'Create'}
                          </Button>
                        </Stack>
                      </Stack>
                    </Card>
                  )}
                </Stack>
              </FormControl>
            </Grid>
          </Grid>
        </Card>

        {/* Payment Information */}
        <Card>
          <Typography level="title-lg" sx={{ mb: 2 }}>
            Payment Information
          </Typography>
          <Grid container spacing={2}>
            <Grid xs={12} md={6}>
              <FormControl>
                <FormLabel>Payment Terms</FormLabel>
                <Select
                  value={formData.payment_terms}
                  onChange={(_, value) => handleChange('payment_terms', value || 'net_30')}
                >
                  <Option value="due_on_receipt">Due on Receipt</Option>
                  <Option value="net_15">Net 15</Option>
                  <Option value="net_30">Net 30</Option>
                  <Option value="net_45">Net 45</Option>
                  <Option value="net_60">Net 60</Option>
                  <Option value="custom">Custom</Option>
                </Select>
              </FormControl>
            </Grid>
            <Grid xs={12} md={6}>
              <FormControl>
                <FormLabel>Preferred Payment Method</FormLabel>
                <Select
                  value={formData.preferred_payment_method}
                  onChange={(_, value) => handleChange('preferred_payment_method', value || 'check')}
                >
                  <Option value="check">Check</Option>
                  <Option value="ach">ACH Transfer</Option>
                  <Option value="wire">Wire Transfer</Option>
                  <Option value="credit_card">Credit Card</Option>
                  <Option value="cash">Cash</Option>
                </Select>
              </FormControl>
            </Grid>
            {formData.payment_terms === 'custom' && (
              <Grid xs={12}>
                <FormControl>
                  <FormLabel>Custom Payment Terms</FormLabel>
                  <Input
                    value={formData.custom_payment_terms}
                    onChange={(e) => handleChange('custom_payment_terms', e.target.value)}
                    placeholder="Describe custom payment terms"
                  />
                </FormControl>
              </Grid>
            )}
          </Grid>
        </Card>

        {/* Status and Notes */}
        <Card>
          <Typography level="title-lg" sx={{ mb: 2 }}>
            Status & Notes
          </Typography>
          <Grid container spacing={2}>
            <Grid xs={12} md={4}>
              <FormControl>
                <FormLabel>Status</FormLabel>
                <Select
                  value={formData.status}
                  onChange={(_, value) => handleChange('status', value || 'active')}
                >
                  <Option value="active">Active</Option>
                  <Option value="inactive">Inactive</Option>
                  <Option value="suspended">Suspended</Option>
                  <Option value="pending">Pending Approval</Option>
                </Select>
              </FormControl>
            </Grid>
            <Grid xs={12} md={4}>
              <FormControl>
                <FormLabel>Rating</FormLabel>
                <Select
                  value={formData.rating?.toString() || ''}
                  onChange={(_, value) => handleChange('rating', value ? Number(value) : undefined)}
                >
                  <Option value="">Not Rated</Option>
                  <Option value={1}>1 Star</Option>
                  <Option value={2}>2 Stars</Option>
                  <Option value={3}>3 Stars</Option>
                  <Option value={4}>4 Stars</Option>
                  <Option value={5}>5 Stars</Option>
                </Select>
              </FormControl>
            </Grid>
            <Grid xs={12}>
              <FormControl>
                <FormLabel>Internal Notes</FormLabel>
                <Textarea
                  minRows={3}
                  value={formData.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  placeholder="Add any internal notes about this subcontractor"
                />
              </FormControl>
            </Grid>
          </Grid>
        </Card>

        {/* Form Actions */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            color="neutral"
            startDecorator={<Cancel />}
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            startDecorator={<Save />}
            loading={saveMutation.isPending}
          >
            {isEditMode ? 'Update' : 'Create'} Subcontractor
          </Button>
        </Box>

        {/* Error Alert */}
        {saveMutation.isError && (
          <Alert color="danger">
            Error saving subcontractor. Please check the form and try again.
          </Alert>
        )}
      </Stack>
    </form>
  );
};